import { Fragment, useMemo } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";

const normalizeOptions = (options = []) =>
  options.map((option) =>
    typeof option === "string"
      ? { label: option, value: option }
      : {
          label: option?.label ?? option?.name ?? String(option?.value ?? ""),
          value: option?.value ?? option?.name ?? option?.label ?? "",
        }
  );

function AppSelect({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  disabled = false,
  containerClassName = "",
  buttonClassName = "",
  buttonPrefix = "",
  optionsClassName = "",
  optionClassName = "",
  showCheckIcon = true,
}) {
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedOption = normalizedOptions.find((option) => option.value === value);

  return (
    <Listbox value={value || ""} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className={`relative ${containerClassName}`}>
          <Listbox.Button
            className={`flex w-full items-center justify-between rounded-xl border border-[#D0D5DD] bg-white px-4 py-3 text-left text-base font-medium text-[#344054] shadow-sm transition focus:border-[#61013B] focus:outline-none focus:ring-2 focus:ring-[#61013B]/10 disabled:cursor-not-allowed disabled:bg-[#F9FAFB] ${buttonClassName}`}
          >
            <span className={selectedOption ? "text-[#344054]" : "text-[#98A2B3]"}>
              {buttonPrefix ? (
                <span className="text-gray-500">{buttonPrefix} </span>
              ) : null}
              {selectedOption?.label || placeholder}
            </span>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-[#667085] transition-transform ${
                open ? "rotate-180" : ""
              }`}
            />
          </Listbox.Button>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Listbox.Options
              className={`absolute left-0 right-0 z-[80] mt-2 max-h-64 overflow-y-auto rounded-xl border border-[#D0D5DD] bg-white py-2 shadow-[0_16px_40px_rgba(16,24,40,0.12)] focus:outline-none ${optionsClassName}`}
            >
              {normalizedOptions.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    `relative cursor-pointer select-none px-4 py-3 text-base font-medium ${
                      active ? "bg-[#F9FAFB] text-[#101828]" : "bg-white text-[#344054]"
                    } ${optionClassName}`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate ${selected ? "font-semibold" : ""}`}>
                        {option.label}
                      </span>
                      {selected && showCheckIcon ? (
                        <span className="absolute inset-y-0 right-4 flex items-center text-[#61013B]">
                          <Check className="h-4 w-4" />
                        </span>
                      ) : null}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}

export default AppSelect;
